import type { LevelDef } from '../content/levelSchema';
import { CAMPAIGN_LEVELS } from '../content/levels/campaign';
import {
  FIRST_HAUL,
  planSecretYards,
  REVISIT_COINS,
  withSecretYard,
  type SecretYard,
} from '../gen/secretYards';
import type { Save } from '../meta/save/schema';

/**
 * The campaign's secret yards as the player finds them (gen/secretYards lays them out): the first
 * haul until it has been banked by clearing the lawn, a few coins after that, and on the pedestal
 * an escaped rare find when one is out there (else, the first time, a hoard).
 */

let plan: Map<number, SecretYard> | null = null;

/** Every secret yard, by campaign lawn number (worked out once). */
export function secretYards(): Map<number, SecretYard> {
  plan ??= planSecretYards((n) => CAMPAIGN_LEVELS[n - 1]?.sim);
  return plan;
}

/** The lawn's secret yard, or null. */
export const secretYardOn = (lawn: number): SecretYard | null => secretYards().get(lawn) ?? null;

/**
 * A campaign lawn with its secret yard stocked from the save. `rareItem`: the escaped find waiting
 * on the pedestal (only when the lawn has no find of its own on it now).
 */
export function lawnWithYard(
  def: LevelDef,
  lawn: number,
  save: Readonly<Save>,
): { level: LevelDef; yard: SecretYard | null; rareItem: string | null } {
  const yard = secretYardOn(lawn);
  if (!yard) return { level: def, yard: null, rareItem: null };
  const first = !save.secretYards.includes(def.id);
  const lawnHasFind = (def.sim.pickups ?? []).some((p) => p.kind === 'rare');
  const rareItem = lawnHasFind
    ? null
    : (save.rareWandering.find((w) => !save.rareFinds.includes(w.item))?.item ?? null);
  const pedestal = rareItem ? 'rare' : first ? 'hoard' : null;
  const sim = withSecretYard(def.sim, yard, {
    coins: first ? FIRST_HAUL : REVISIT_COINS,
    pedestal,
  });
  return { level: { ...def, sim }, yard, rareItem };
}
